import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Save,
  AlertTriangle,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getMeetingModules } from "@/store/slices/meetingModulesSlice";
import { getMeetingSections } from "@/store/slices/meetingSectionSlice";
import {
  savePreparationAnswer,
  loadPreparationAnswers,
  clearPreparationAnswers,
} from "@/store/slices/meetingSectionSlice";
import { selectModuleById } from "@/store/selectors/meetingModulesSelectors";
import {
  selectAllComplements,
  selectSectionsLoading,
  selectPreparationAnswers,
} from "@/store/selectors/meetingSectionSelectors";
import {
  loadPreparationData,
  clearPreparationData,
  validateAnswer,
} from "@/utils/sessionStorageManager";
import { MeetingSessionManager } from "@/utils/meetingSessionManager";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/utils/logger";

const MIN_ANSWER_LENGTH = 30;

const MeetingPreparation = () => {
  const { meetingId } = useParams<{ meetingId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Ensure meeting session exists (create if needed)
  useEffect(() => {
    if (meetingId) {
      const existingSession = MeetingSessionManager.get(meetingId);
      if (!existingSession) {
        // Create session if it doesn't exist
        MeetingSessionManager.create(meetingId);
        logger.log(
          "[MeetingPreparation] Created missing meeting session for:",
          meetingId
        );
      }
    }
  }, [meetingId]);
  const { toast } = useToast();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [showValidation, setShowValidation] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);

  // Get data from Redux
  const moduleData = useAppSelector(selectModuleById(meetingId || ""));
  const complements = useAppSelector(selectAllComplements);
  const loading = useAppSelector(selectSectionsLoading);
  const savedAnswers = useAppSelector(selectPreparationAnswers);

  // Fetch data on mount
  useEffect(() => {
    dispatch(getMeetingModules({}));
  }, [dispatch]);

  useEffect(() => {
    if (meetingId) {
      dispatch(getMeetingSections(meetingId));
    }
  }, [meetingId, dispatch]);

  // Load from sessionStorage after sections are loaded
  useEffect(() => {
    if (complements.length > 0) {
      const sessionData = loadPreparationData();
      if (sessionData && sessionData.moduleId === meetingId) {
        dispatch(loadPreparationAnswers(sessionData.answers));
        logger.log("[MeetingPreparation] Loaded answers from sessionStorage");
      }
    }
  }, [complements.length, meetingId, dispatch]);

  // NOTE: We do NOT clear sessionStorage on unmount here
  // The data must persist until successfully submitted in MeetingPreparationSummary
  // It will be cleared there after successful submission

  // Warn user before closing tab/window if has unprocessed changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue =
          "You have unprocessed answers. Are you sure you want to leave?";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Track if there are any answers
  useEffect(() => {
    const hasAnswers = Object.values(savedAnswers).some(
      (answer) => answer.trim().length > 0
    );
    setHasUnsavedChanges(hasAnswers);
  }, [savedAnswers]);

  // Loading state
  if (loading || !moduleData || complements.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            Loading preparation questions...
          </p>
        </div>
      </div>
    );
  }

  const { module, category } = moduleData;
  const totalQuestions = complements.length;
  const currentComplement = complements[currentQuestion];
  const currentAnswer = savedAnswers[currentComplement.id] || "";
  const isCurrentAnswerValid = validateAnswer(currentAnswer, MIN_ANSWER_LENGTH);
  const characterCount = currentAnswer.trim().length;

  const handleNext = () => {
    // Validate current answer before proceeding
    if (!isCurrentAnswerValid) {
      setShowValidation(true);
      toast({
        title: "Answer required",
        description: `Please provide an answer with at least ${MIN_ANSWER_LENGTH} characters.`,
        variant: "destructive",
      });
      return;
    }

    setShowValidation(false);

    if (currentQuestion < totalQuestions - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Navigate to summary page
      navigate(`/meeting/${meetingId}/summary`);
    }
  };

  const handleBack = () => {
    setShowValidation(false);
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else {
      // Ask for confirmation before leaving if has unprocessed answers
      if (hasUnsavedChanges) {
        setShowExitDialog(true);
      } else {
        navigate(`/meeting/${meetingId}`);
      }
    }
  };

  const handleConfirmExit = () => {
    setShowExitDialog(false);
    navigate(`/meeting/${meetingId}`);
  };

  const handleCancelExit = () => {
    setShowExitDialog(false);
  };

  const updateAnswer = (value: string) => {
    dispatch(
      savePreparationAnswer({
        complementId: currentComplement.id,
        answer: value,
      })
    );
    // Reset validation state when user types
    if (showValidation && value.trim().length >= MIN_ANSWER_LENGTH) {
      setShowValidation(false);
    }
  };

  const progress = ((currentQuestion + 1) / totalQuestions) * 100;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to={`/meeting/${meetingId}`}>
          <Button variant="ghost" className="mb-8 group">
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back
          </Button>
        </Link>

        <div className="animate-fade-in space-y-8">
          <div className="space-y-4">
            <div className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium">
              {category.name}
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground">
              {module.module_name}
            </h1>
            <div className="flex items-center gap-4">
              <Progress value={progress} className="flex-1" />
              <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
                {currentQuestion + 1} of {totalQuestions}
              </span>
            </div>
          </div>

          <Card className="border-border/50 backdrop-blur-sm bg-card/80 animate-scale-in">
            <div className="p-8">
              <div className="space-y-6">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-sm font-bold text-primary">
                      {currentQuestion + 1}
                    </span>
                  </div>
                  <div className="flex-1 space-y-3">
                    <div className="flex items-start justify-between">
                      <h2 className="text-xl font-semibold text-foreground flex-1">
                        {currentComplement.complement}
                      </h2>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground ml-4">
                        <Save className="w-3 h-3" />
                        <span>Auto-saved</span>
                      </div>
                    </div>
                    <div className="relative">
                      <Textarea
                        value={currentAnswer}
                        onChange={(e) => updateAnswer(e.target.value)}
                        placeholder="Type your answer in any language...."
                        className={`min-h-[200px] resize-none ${
                          showValidation && !isCurrentAnswerValid
                            ? "border-destructive focus-visible:ring-destructive"
                            : ""
                        }`}
                      />
                      <div className="flex items-center justify-between mt-2">
                        <span
                          className={`text-xs ${
                            characterCount >= MIN_ANSWER_LENGTH
                              ? "text-green-600 dark:text-green-400"
                              : showValidation
                                ? "text-destructive"
                                : "text-muted-foreground"
                          }`}
                        >
                          {characterCount} / {MIN_ANSWER_LENGTH} characters
                          minimum
                        </span>
                        {showValidation && !isCurrentAnswerValid && (
                          <span className="text-xs text-destructive">
                            Please provide a complete answer
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <div className="flex justify-between items-center">
            <Button variant="outline" onClick={handleBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {currentQuestion === 0 ? "Back to Conversation" : "Previous"}
            </Button>
            <Button onClick={handleNext} disabled={!isCurrentAnswerValid}>
              {currentQuestion === totalQuestions - 1 ? (
                <>
                  Finish Preparation
                  <CheckCircle2 className="w-4 h-4 ml-2" />
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Exit Confirmation Dialog */}
      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-warning" />
              Leave Preparation?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You have unprocessed answers that haven't been submitted yet. If
              you leave now, you'll need to complete the preparation again.
              <br />
              <br />
              Are you sure you want to leave?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelExit}>
              Stay and Continue
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmExit}
              className="bg-destructive hover:bg-destructive/90"
            >
              Yes, Leave
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MeetingPreparation;
